// PrivacyToggle — visual switch for shared vs. private diary.
//
// Per W2 §4.1 (privacy = "only author can see"); the server enforces this
// regardless of what the client sends.  Toggle is purely a UX signal.

import { cn } from "@/lib/utils/cn";

interface PrivacyToggleProps {
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

export function PrivacyToggle({ value, onChange, disabled }: PrivacyToggleProps) {
  return (
    <div
      role="group"
      aria-label="日記隱私設定"
      data-testid="privacy-toggle"
      data-privacy={value ? "private" : "shared"}
      className="flex flex-col gap-2"
    >
      <div className="font-label text-ink-2">誰能看這篇日記?</div>
      <div className="flex p-1 bg-paper-deep rounded-md border border-hairline">
        <button
          type="button"
          aria-pressed={!value}
          disabled={disabled}
          onClick={() => onChange(false)}
          data-testid="privacy-shared"
          className={cn(
            // `min-h-11` is what makes this a 44pt target; `py-1.5` alone gave a
            // 36px-tall button, measured at every viewport in W46.0. The
            // segmented-control chrome stays 36px visually because the pill is
            // painted on the button's own background, not on its box — same
            // axis split as `.shell-btn`, and it costs nothing here because the
            // two segments are `flex-1` and never compete for width.
            "flex-1 press min-h-11 px-3 py-1.5 rounded text-body-sm transition-colors",
            !value ? "bg-paper text-ink-1 shadow-sm" : "text-ink-3 hover:text-ink-2",
          )}
        >
          共享 (對方也能看)
        </button>
        <button
          type="button"
          aria-pressed={value}
          disabled={disabled}
          onClick={() => onChange(true)}
          data-testid="privacy-private"
          className={cn(
            "flex-1 press min-h-11 px-3 py-1.5 rounded text-body-sm transition-colors",
            value ? "bg-paper text-ink-1 shadow-sm" : "text-ink-3 hover:text-ink-2",
          )}
        >
          私人 (只有我自己)
        </button>
      </div>
      <p className="text-body-sm text-ink-3" data-testid="privacy-help">
        {value ? "私人日記只對你顯示;對方看不到內容。" : "共享日記會顯示在你們雙方的時間軸上。"}
      </p>
    </div>
  );
}
